import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { speakersApi } from '../../api/speakers.js';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { SPEAKER_STATUSES } from '../../utils/constants.js';

export function SpeakerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  usePageHeader(isEdit ? 'Edit speaker' : 'Add speaker');

  const [form, setForm] = useState({
    firstName: '', lastName: '', preferredName: '', email: '',
    phone: '', organization: '', jobTitle: '', bio: '',
    topicAreas: '', websiteUrl: '', notes: '',
    // Pre-booking pipeline — only meaningful while status is Potential/
    // Contacted/Interested, but always editable (a speaker can move back
    // into the pipeline for a new opportunity even after being Active).
    status: 'Active',
    topic: '', source: '', lastContactDate: '', nextFollowUpDate: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    speakersApi.get(id)
      .then((s) => setForm({
        firstName: s.firstName ?? '',
        lastName: s.lastName ?? '',
        preferredName: s.preferredName ?? '',
        email: s.email ?? '',
        phone: s.phone ?? '',
        organization: s.organization ?? '',
        jobTitle: s.jobTitle ?? '',
        bio: s.bio ?? '',
        topicAreas: (s.topicAreas ?? []).join(', '),
        websiteUrl: s.websiteUrl ?? '',
        notes: s.notes ?? '',
        status: s.status ?? 'Active',
        topic: s.topic ?? '',
        source: s.source ?? '',
        lastContactDate: s.lastContactDate ?? '',
        nextFollowUpDate: s.nextFollowUpDate ?? '',
      }))
      .catch(setLoadError)
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        topicAreas: form.topicAreas.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) await speakersApi.update(id, payload);
      else await speakersApi.create(payload);
      navigate('/speakers');
    } catch (err) {
      setErrors({ _: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="page-body">
        <ErrorAlert error={loadError ?? errors._} />
        <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 640 }}>
          <div className="stack">
            <div className="grid-2">
              <FormField label="First name" required id="firstName" error={errors.firstName}>
                <Input id="firstName" value={form.firstName} onChange={set('firstName')} error={errors.firstName} required />
              </FormField>
              <FormField label="Last name" required id="lastName" error={errors.lastName}>
                <Input id="lastName" value={form.lastName} onChange={set('lastName')} error={errors.lastName} required />
              </FormField>
            </div>
            <FormField label="Preferred name" id="preferredName" hint="Name to use in emails if different from first name">
              <Input id="preferredName" value={form.preferredName} onChange={set('preferredName')} />
            </FormField>
            <FormField label="Email" required id="email" error={errors.email}>
              <Input id="email" type="email" value={form.email} onChange={set('email')} error={errors.email} required />
            </FormField>
            <div className="grid-2">
              <FormField label="Phone" id="phone">
                <Input id="phone" type="tel" value={form.phone} onChange={set('phone')} />
              </FormField>
              <FormField label="Website / LinkedIn" id="websiteUrl">
                <Input id="websiteUrl" type="url" value={form.websiteUrl} onChange={set('websiteUrl')} />
              </FormField>
            </div>
            <div className="grid-2">
              <FormField label="Organization" id="organization">
                <Input id="organization" value={form.organization} onChange={set('organization')} />
              </FormField>
              <FormField label="Job title" id="jobTitle">
                <Input id="jobTitle" value={form.jobTitle} onChange={set('jobTitle')} />
              </FormField>
            </div>
            <FormField label="Topic areas" id="topicAreas" hint="Comma-separated">
              <Input id="topicAreas" value={form.topicAreas} onChange={set('topicAreas')} placeholder="Agile, Risk Management, Leadership" />
            </FormField>
            <FormField label="Biography" id="bio">
              <Textarea id="bio" value={form.bio} onChange={set('bio')} rows={5} />
            </FormField>
            <FormField label="Notes" id="notes">
              <Textarea id="notes" value={form.notes} onChange={set('notes')} rows={3} />
            </FormField>

            <div className="card stack-2">
              <h2 style={{ fontSize: 'var(--text-base)' }}>Pipeline</h2>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>
                Where this person stands before (or between) speaking engagements. Booking them for an
                event — from their profile page — automatically moves status to Active.
              </p>
              <FormField label="Status" id="status">
                <Select id="status" value={form.status} onChange={set('status')}>
                  {SPEAKER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormField>
              <div className="grid-2">
                <FormField label="Topic / proposed subject" id="topic">
                  <Input id="topic" value={form.topic} onChange={set('topic')} />
                </FormField>
                <FormField label="Source / referral" id="source">
                  <Input id="source" value={form.source} onChange={set('source')} />
                </FormField>
              </div>
              <div className="grid-2">
                <FormField label="Last contact date" id="lastContactDate">
                  <Input id="lastContactDate" type="date" value={form.lastContactDate} onChange={set('lastContactDate')} />
                </FormField>
                <FormField label="Next follow-up date" id="nextFollowUpDate">
                  <Input id="nextFollowUpDate" type="date" value={form.nextFollowUpDate} onChange={set('nextFollowUpDate')} />
                </FormField>
              </div>
            </div>

            <div className="inline" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add speaker'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
